// Produces a clean A4 invoice document in a hidden iframe and triggers print.
// The exact same HTML is used to email the invoice so the customer sees the
// identical layout. Returns the generated HTML string for the email path.
import { fileUrl } from "../lib/files";

const fmtGBP = (n) =>
  `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  // Accepts "YYYY-MM-DD" or full ISO
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

const escapeHtml = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const nl2br = (s = "") => escapeHtml(s).replace(/\n/g, "<br/>");

const absoluteLogo = () => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/logo.png`;
};

export const buildInvoiceHtml = ({ invoice, settings, logoUrl }) => {
  const lineRows = invoice.lines
    .map((l) => {
      const amount = Number(l.quantity) * Number(l.unit_price);
      return `
      <tr>
        <td class="desc">${escapeHtml(l.description)}</td>
        <td class="qty">${Number(l.quantity)}</td>
        <td class="price">${fmtGBP(l.unit_price)}</td>
        <td class="amount">${fmtGBP(amount)}</td>
      </tr>`;
    })
    .join("");

  const vatRow = invoice.vat_enabled
    ? `
      <tr>
        <td class="total-label">VAT ${(invoice.vat_rate * 100).toFixed(0)}%</td>
        <td class="total-amount">${fmtGBP(invoice.vat_amount)}</td>
      </tr>`
    : "";

  const vatLine =
    invoice.vat_enabled && settings.vat_number
      ? `<p class="meta">VAT No: <strong>${escapeHtml(settings.vat_number)}</strong></p>`
      : "";

  const bank = settings;
  const bankBits = [
    bank.bank_name && `<div><span>Bank</span><strong>${escapeHtml(bank.bank_name)}</strong></div>`,
    bank.account_name && `<div><span>Account name</span><strong>${escapeHtml(bank.account_name)}</strong></div>`,
    bank.sort_code && `<div><span>Sort code</span><strong>${escapeHtml(bank.sort_code)}</strong></div>`,
    bank.account_number && `<div><span>Account number</span><strong>${escapeHtml(bank.account_number)}</strong></div>`,
    bank.iban && `<div><span>IBAN</span><strong>${escapeHtml(bank.iban)}</strong></div>`,
  ]
    .filter(Boolean)
    .join("");

  const bankBlock = bankBits
    ? `
    <section class="bank">
      <h3>Payment Details</h3>
      <div class="bank-grid">${bankBits}</div>
    </section>`
    : "";

  const notesBlock = invoice.notes
    ? `<section class="notes"><h3>Notes</h3><p>${nl2br(invoice.notes)}</p></section>`
    : "";

  const termsBlock = invoice.payment_terms
    ? `<section class="notes"><h3>Payment Terms</h3><p>${nl2br(invoice.payment_terms)}</p></section>`
    : "";

  const statusChip =
    (() => {
      const payments = invoice.payments || [];
      let paid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
      const total = Number(invoice.total || 0);
      if (invoice.status === "paid" && payments.length === 0) paid = total;
      if (invoice.status === "draft") return '<span class="chip chip-draft">DRAFT</span>';
      if (invoice.status === "paid" || (total > 0 && paid >= total)) return '<span class="chip chip-paid">PAID</span>';
      if (paid > 0) return '<span class="chip chip-part">PART PAID</span>';
      return '<span class="chip chip-sent">UNPAID</span>';
    })();

  const payments = (invoice.payments || [])
    .slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  let paidSum = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  if (invoice.status === "paid" && payments.length === 0) paidSum = Number(invoice.total || 0);
  const balanceDueAmt = Math.max(0, Number(invoice.total || 0) - paidSum);

  const paymentsBlock = payments.length
    ? `
    <section class="payments">
      <h3>Payment History</h3>
      <table class="paylist">
        <thead>
          <tr>
            <th class="pdate">Date</th>
            <th class="pmethod">Method</th>
            <th class="pnote">Note</th>
            <th class="pamount">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${payments.map((p) => `
            <tr>
              <td>${fmtDDMMYYYY(p.date)}</td>
              <td>${escapeHtml(p.method || "")}</td>
              <td>${escapeHtml(p.note || "")}</td>
              <td class="pamount">${fmtGBP(p.amount)}</td>
            </tr>`).join("")}
        </tbody>
      </table>
    </section>`
    : "";

  const logo = logoUrl && logoUrl.startsWith("http") ? logoUrl : logoUrl ? fileUrl(logoUrl) : absoluteLogo();

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"/>
<title>Invoice ${escapeHtml(invoice.number)} — ${escapeHtml(settings.company_name || "Elin's Consultancy")}</title>
<style>
@page { size: A4; margin: 14mm 14mm; }
*{box-sizing:border-box;}
html,body{margin:0;padding:0;background:#F5F0E6;color:#1C2620;font-family:'Manrope','Helvetica Neue',Arial,sans-serif;font-size:12.5px;line-height:1.55;}
.page{max-width:760px;margin:0 auto;padding:34px 38px;background:#fff;border:1px solid #e4d9bd;}
header{display:flex;justify-content:space-between;align-items:flex-start;gap:28px;padding-bottom:22px;border-bottom:1px solid #e4d9bd;}
.brand{display:flex;align-items:center;gap:14px;}
.brand img{height:56px;width:auto;}
.brand .co{font-family:'Cormorant Garamond',Georgia,serif;font-size:22px;color:#0F3226;letter-spacing:.08em;line-height:1;}
.brand .co small{display:block;font-family:'Manrope',sans-serif;letter-spacing:.26em;font-size:9.5px;color:#8a7a4f;margin-top:5px;font-weight:700;text-transform:uppercase;}
.doc-title{text-align:right;}
.doc-title h1{font-family:'Cormorant Garamond',Georgia,serif;font-weight:500;font-size:34px;color:#0F3226;margin:0;line-height:1;}
.doc-title .num{font-size:12px;letter-spacing:.2em;text-transform:uppercase;color:#8a7a4f;font-weight:700;margin-top:8px;}
.chips{margin-top:10px;}
.chip{display:inline-block;font-size:10px;letter-spacing:.22em;font-weight:700;padding:5px 10px;}
.chip-paid{background:#0F3226;color:#F5F0E6;}
.chip-sent{background:#faf0d3;color:#8a6a1f;border:1px solid #e4d9bd;}
.chip-part{background:#f3e8c6;color:#8a6a1f;border:1px solid #d9c689;}
.chip-draft{background:#eee;color:#555;}
.row{display:grid;grid-template-columns:1fr 1fr;gap:30px;padding:22px 0;border-bottom:1px solid #e4d9bd;}
.row h3{font-size:9.5px;letter-spacing:.24em;text-transform:uppercase;color:#8a7a4f;font-weight:700;margin:0 0 10px;}
.row p{margin:0;color:#1C2620;line-height:1.65;}
.row .meta{font-size:11px;color:#4D6359;margin-top:6px;}
.dates{display:flex;justify-content:space-between;gap:30px;padding:16px 0 20px;}
.dates .cell{flex:1;}
.dates .cell small{display:block;font-size:9.5px;letter-spacing:.22em;text-transform:uppercase;color:#8a7a4f;font-weight:700;margin-bottom:4px;}
.dates .cell strong{font-family:'Cormorant Garamond',Georgia,serif;font-size:18px;color:#0F3226;}
table.items{width:100%;border-collapse:collapse;font-size:12.5px;margin-top:4px;}
.items thead th{text-align:left;background:#faf4e3;color:#8a7a4f;font-size:9.5px;letter-spacing:.22em;text-transform:uppercase;font-weight:700;padding:11px 12px;border-bottom:1px solid #e4d9bd;}
.items thead th.qty,.items thead th.price,.items thead th.amount{text-align:right;}
.items tbody td{padding:12px;border-bottom:1px solid #efe6cd;color:#1C2620;}
.items td.qty,.items td.price,.items td.amount{text-align:right;}
.items td.amount{font-weight:600;}
.totals{margin-top:4px;display:flex;justify-content:flex-end;}
.totals table{border-collapse:collapse;min-width:260px;}
.totals td{padding:8px 12px;}
.totals .total-label{text-align:left;color:#4D6359;font-size:11.5px;letter-spacing:.14em;text-transform:uppercase;font-weight:700;}
.totals .total-amount{text-align:right;font-size:14px;font-weight:600;color:#1C2620;}
.totals tr.grand td{border-top:1px solid #e4d9bd;padding-top:14px;}
.totals tr.grand .total-label{color:#0F3226;font-size:12px;}
.totals tr.grand .total-amount{font-family:'Cormorant Garamond',Georgia,serif;font-size:24px;color:#a67a1d;}
.notes{margin-top:22px;padding-top:16px;border-top:1px solid #e4d9bd;}
.notes h3{font-size:9.5px;letter-spacing:.24em;text-transform:uppercase;color:#8a7a4f;font-weight:700;margin:0 0 6px;}
.notes p{margin:0;color:#4D6359;font-size:12px;line-height:1.7;}
.bank{margin-top:22px;padding:18px 20px;background:#faf4e3;border:1px solid #e4d9bd;}
.bank h3{font-size:10px;letter-spacing:.26em;text-transform:uppercase;color:#8a6a1f;font-weight:700;margin:0 0 10px;}
.bank-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px 24px;}
.bank-grid > div{display:flex;flex-direction:column;}
.bank-grid span{font-size:9.5px;letter-spacing:.2em;text-transform:uppercase;color:#8a7a4f;font-weight:700;}
.bank-grid strong{font-size:13px;color:#0F3226;letter-spacing:.02em;}
.payments{margin-top:22px;padding-top:16px;border-top:1px solid #e4d9bd;}
.payments h3{font-size:9.5px;letter-spacing:.24em;text-transform:uppercase;color:#8a7a4f;font-weight:700;margin:0 0 10px;}
table.paylist{width:100%;border-collapse:collapse;font-size:12px;}
.paylist thead th{text-align:left;color:#8a7a4f;font-size:9.5px;letter-spacing:.22em;text-transform:uppercase;font-weight:700;padding:8px 10px;border-bottom:1px solid #e4d9bd;}
.paylist thead th.pamount{text-align:right;}
.paylist tbody td{padding:9px 10px;border-bottom:1px solid #efe6cd;color:#1C2620;}
.paylist tbody td.pamount{text-align:right;font-weight:600;}
footer{margin-top:22px;padding-top:14px;border-top:1px solid #e4d9bd;font-size:10.5px;color:#8a8f86;line-height:1.65;display:flex;justify-content:space-between;gap:16px;}
@media print { html, body { background:#fff; } .page{border:none;padding:0;max-width:none;} }
</style></head>
<body>
  <div class="page">
    <header>
      <div class="brand">
        <img src="${logo}" alt="${escapeHtml(settings.company_name || "Elin's Consultancy")} logo"/>
        <div class="co">${escapeHtml((settings.company_name || "Elin's Consultancy").toUpperCase())}
          <small>Property Consultancy</small>
        </div>
      </div>
      <div class="doc-title">
        <h1>Invoice</h1>
        <p class="num">${escapeHtml(invoice.number)}</p>
        <div class="chips">${statusChip}</div>
      </div>
    </header>

    <div class="row">
      <div>
        <h3>From</h3>
        <p><strong>${escapeHtml(settings.company_name || "Elin's Consultancy")}</strong></p>
        ${settings.company_address ? `<p>${nl2br(settings.company_address)}</p>` : ""}
        ${settings.company_email ? `<p class="meta">${escapeHtml(settings.company_email)}</p>` : ""}
        ${settings.company_phone ? `<p class="meta">${escapeHtml(settings.company_phone)}</p>` : ""}
        ${vatLine}
      </div>
      <div>
        <h3>Bill To</h3>
        <p><strong>${escapeHtml(invoice.customer_name)}</strong></p>
        ${invoice.customer_company ? `<p>${escapeHtml(invoice.customer_company)}</p>` : ""}
        ${invoice.customer_address ? `<p>${nl2br(invoice.customer_address)}</p>` : ""}
        ${invoice.customer_email ? `<p class="meta">${escapeHtml(invoice.customer_email)}</p>` : ""}
      </div>
    </div>

    <div class="dates">
      <div class="cell"><small>Invoice Date</small><strong>${fmtDDMMYYYY(invoice.invoice_date)}</strong></div>
      <div class="cell"><small>Due Date</small><strong>${fmtDDMMYYYY(invoice.due_date)}</strong></div>
      <div class="cell"><small>Balance Due</small><strong>${fmtGBP(balanceDueAmt)}</strong></div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th class="desc">Description</th>
          <th class="qty">Qty</th>
          <th class="price">Unit Price</th>
          <th class="amount">Amount</th>
        </tr>
      </thead>
      <tbody>${lineRows}</tbody>
    </table>

    <div class="totals">
      <table>
        <tr><td class="total-label">Subtotal</td><td class="total-amount">${fmtGBP(invoice.subtotal)}</td></tr>
        ${vatRow}
        <tr><td class="total-label">Invoice Total</td><td class="total-amount">${fmtGBP(invoice.total)}</td></tr>
        ${paidSum > 0 ? `<tr><td class="total-label">Paid To Date</td><td class="total-amount">${fmtGBP(paidSum)}</td></tr>` : ""}
        <tr class="grand"><td class="total-label">Balance Due</td><td class="total-amount">${fmtGBP(balanceDueAmt)}</td></tr>
      </table>
    </div>

    ${paymentsBlock}

    ${termsBlock}
    ${notesBlock}
    ${bankBlock}

    <footer>
      <div>Thank you for your business.</div>
      <div>${escapeHtml(settings.company_name || "Elin's Consultancy")}${settings.company_email ? ` · ${escapeHtml(settings.company_email)}` : ""}</div>
    </footer>
  </div>
</body></html>`;
};

export const printInvoice = (html) => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  const trigger = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (e) {
      /* no-op */
    }
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1500);
  };

  if (iframe.contentDocument?.readyState === "complete") {
    setTimeout(trigger, 300);
  } else {
    iframe.addEventListener("load", () => setTimeout(trigger, 300));
    setTimeout(trigger, 800);
  }
};

export { fmtDDMMYYYY, fmtGBP };
